import { User } from "@prisma/client";
import { Application } from "@prisma/client";
import { Comment } from "@prisma/client";
import Badge, { BadgeType } from "immigram-ui-cmon/src/components/Badge/Badge";
import Button, {
  ButtonType,
} from "immigram-ui-cmon/src/components/Button/Button";
import Comments from "immigram-ui-cmon/src/components/Comments/Comments";
import { IComment } from "immigram-ui-cmon/src/components/Comments/types";
import {
  Icon24Check,
  Icon24Clock,
} from "immigram-ui-cmon/src/components/Icons";
import { showNotification } from "immigram-ui-cmon/src/components/Notification/Notification";
import clsxm from "immigram-ui-cmon/src/lib/clsxm";
import isNumber from "lodash/isNumber";
import { useTranslation } from "next-i18next";
import { useCallback, useEffect, useMemo, useState } from "react";

import { ApplicationSectionStatusType } from "@/lib/applicationSectionStatus";
import { isAdmin } from "@/lib/auth/rbac";
import { ExperienceType } from "@/lib/experienceType";
import useBooleanState from "@/lib/hook/useBooleanState";
import useTransparentState from "@/lib/hook/useTransparentState";
import { Event, useTracking } from "@/lib/tracking";

import { ImmigramFormConnected } from "@/components/ImmigramForm/ImmigramFormConnected";
import { ConfirmModalIconType } from "@/components/Modal/ConfirmModal";
import FormModal from "@/components/Modal/FormModal";
import { showConfirmModal } from "@/components/Modal/ModalController";
import ValidationModal from "@/components/Modal/ValidationModal";
import { DETAILS_EXP_TYPE_TO_NAMESPACE } from "@/containers/ProfJourney";

import { useApplicationForm } from "@/core/client/application/applicationForm/useApplicationForm";
import {
  useComments,
  useDeleteCommentMutation,
  usePublishCommentsMutation,
  useUpsertCommentMutation,
} from "@/core/client/comments/comments";

interface ProfJourneyProjectDetailsModalProps {
  application: Application;
  ownUser: User;
  experienceType?: ExperienceType;
  formInstanceId?: string;
  formParentId?: string;
  formRootId?: string;
  open?: boolean;
  position?: string;
  projectName?: string;
  projectStatus?: ApplicationSectionStatusType | null;
  editable?: boolean;
  isValid?: boolean;
  showErrors?: boolean;
  externalFormValues?: Record<string, string>;
  onClose: () => void;
  onSave: () => void;
  onFieldChange?: (itemConfig: any, value: string) => void;
  onFormInstanceCreate?: (formInstanceId: string) => void;
  onFinishReview?: ({
    projectName,
    formInstanceId,
    experienceType,
    formRootId,
  }: {
    projectName?: string;
    formInstanceId: string;
    formRootId?: string;
    experienceType?: ExperienceType;
  }) => Promise<void>;
  onSendFeedback?: ({
    projectName,
    formInstanceId,
    experienceType,
  }: {
    projectName?: string;
    formInstanceId: string;
    experienceType?: ExperienceType;
  }) => Promise<void>;
  onRollback?: ({
    formInstanceId,
  }: {
    formInstanceId: string;
  }) => Promise<void>;
  onProjectSubmitToReview?: ({
    projectName,
    formInstanceId,
    experienceType,
  }: {
    projectName?: string;
    formInstanceId: string;
    experienceType?: ExperienceType;
  }) => Promise<void>;
}

export function ProfJourneyProjectDetailsModal({
  open,
  onClose,
  onSave,
  onFieldChange,
  onFormInstanceCreate,
  onProjectSubmitToReview,
  onFinishReview,
  onSendFeedback,
  showErrors,
  isValid = true,
  projectStatus,
  editable,
  application,
  formInstanceId,
  formParentId,
  formRootId,
  ownUser,
  experienceType,
  position,
  projectName,
  externalFormValues,
  onRollback,
}: ProfJourneyProjectDetailsModalProps) {
  const { t } = useTranslation();
  const { trackEvent } = useTracking();

  const [instanceId, setInstanceId] = useTransparentState<string | undefined>(
    formInstanceId
  );

  const handleFormInstanceCreate = useCallback(
    (formInstanceId: string) => {
      setInstanceId(formInstanceId);
      onFormInstanceCreate?.(formInstanceId);
    },
    [onFormInstanceCreate, setInstanceId]
  );

  const { data: formData } = useApplicationForm(
    application.id,
    formInstanceId,
    {
      keepPreviousData: false,
      enabled: !!formInstanceId,
    }
  );

  const formIsLoadingState = useBooleanState(false);

  const [isFormValid, setIsFormValid] = useTransparentState<
    boolean | undefined
  >(isValid);
  const [showFormErrors, setShowFormErrors] = useTransparentState<
    boolean | undefined
  >(showErrors);

  useEffect(() => {
    setShowFormErrors(false);
  }, [open, setShowFormErrors]);

  const [isValidationModalOpen, setIsValidationModalOpen] = useState(false);

  const handleSaveAndCloseClick = useCallback(() => {
    if (isFormValid) {
      onSave();
    } else {
      setShowFormErrors(true);
    }
  }, [isFormValid, onSave, setShowFormErrors]);

  const formNamespace =
    experienceType && DETAILS_EXP_TYPE_TO_NAMESPACE[experienceType];

  const heading = projectName
    ? projectName
    : t(
        "application:profJourney.profJourneyCompanyModal.heading.describeProject"
      );

  const { data: comments } = useComments(application.id);
  const { mutateAsync: publishComments } = usePublishCommentsMutation(
    application.id
  );
  const { mutateAsync: upsertComment } = useUpsertCommentMutation();
  const { mutateAsync: deleteComment } = useDeleteCommentMutation();

  const isUnresolvedComments = useMemo(
    () =>
      comments?.some((item) => {
        if (item.group !== instanceId) return false;

        if (!isAdmin(ownUser) && item.from !== "manager") return false;

        return !item.isResolved && !item.isDraft;
      }),
    [comments, instanceId, ownUser]
  );

  const isUnsentComments = useMemo(
    () =>
      comments?.some((item) => {
        if (item.group !== instanceId) return false;
        if (!item.isDraft) return false;

        // Для админа: показываем кнопку, если есть неотправленные комментарии админа
        // Для клиента: показываем кнопку, если есть неотправленные комментарии клиента
        return item.from === (isAdmin(ownUser) ? "manager" : "client");
      }),
    [comments, instanceId, ownUser]
  );

  const commentsToPublish = useMemo(
    () =>
      comments?.filter(
        (item) => item.group === instanceId && !item.isResolved && item.isDraft
      ) as any as Comment[],
    [comments, instanceId]
  );

  const handeSubmitToReview = useCallback(async () => {
    if (isUnresolvedComments) {
      showConfirmModal({
        gifUrl: "/images/gif/giphy-4.gif",
        heading: t("application:popup.ckeckAllComments.title"),
        text: t("application:popup.ckeckAllComments.text"),
        confirmButtonText: t("application:common.got_it"),
        cancelButtonText: null,
      });
    } else {
      if (isFormValid) {
        showConfirmModal({
          gifUrl: "/images/gif/giphy-11.gif",
          gifBgPosition: "50% 50%",
          heading: t(
            "application:profJourney.profJourneyProjectDetailsModal.submitToReviewModal.heading",
            { projectName }
          ),
          text: t(
            "application:profJourney.profJourneyProjectDetailsModal.submitToReviewModal.text"
          ),
          confirmButtonText: t(
            "application:profJourney.profJourneyProjectDetailsModal.submitToReviewModal.confirmButtonText"
          ),
          onConfirm: async () => {
            try {
              if (!instanceId) {
                throw new Error("No project to submit");
              }
              await onProjectSubmitToReview?.({
                projectName: projectName,
                formInstanceId: instanceId,
                experienceType,
              });

              if (experienceType === ExperienceType.Employment) {
                trackEvent(Event.SubmitForReviewProject, {
                  reviewIteration:
                    formData && isNumber(formData?.reviewIteration)
                      ? formData.reviewIteration + 1
                      : undefined,
                });
              } else if (experienceType === ExperienceType.Entrepreneurship) {
                trackEvent(Event.SubmitForReviewMilestone, {
                  reviewIteration:
                    formData && isNumber(formData?.reviewIteration)
                      ? formData.reviewIteration + 1
                      : undefined,
                });
              }
              onSave();
              onClose();
            } catch (e) {
              showNotification({
                variant: "alert",
                title: t(
                  "application:profJourneyProjectDetailsModal.notification.failedToSubmit"
                ),
              });
            }
          },
        });
      } else {
        setIsValidationModalOpen(true);
        setShowFormErrors(true);
      }
    }
  }, [
    onProjectSubmitToReview,
    onClose,
    instanceId,
    projectName,
    experienceType,
    t,
    trackEvent,
    formData,
    isFormValid,
    onSave,
    setShowFormErrors,
    isUnresolvedComments,
  ]);

  const handleSendFeedback = useCallback(async () => {
    showConfirmModal({
      heading: t(
        "application:profJourneyProjectDetailsModal.sendFeedBack.heading"
      ),
      text: t("application:profJourneyProjectDetailsModal.sendFeedBack.text"),
      confirmButtonText: t(
        "application:profJourneyProjectDetailsModal.sendFeedBack.confirmButtonText"
      ),
      onConfirm: async () => {
        try {
          if (commentsToPublish?.length) {
            await publishComments(commentsToPublish);
          }
          if (!instanceId) {
            throw new Error("No feedback to send");
          }
          await onSendFeedback?.({
            projectName,
            formInstanceId: instanceId,
            experienceType,
          });
          onClose();
        } catch (e) {
          showNotification({
            variant: "alert",
            title: t(
              "application:profJourneyProjectDetailsModal.notification.failedToSendFeedback"
            ),
          });
        }
      },
    });
  }, [
    commentsToPublish,
    instanceId,
    publishComments,
    onSendFeedback,
    projectName,
    experienceType,
    onClose,
    t,
  ]);

  const handleSectionRollback = useCallback(async () => {
    showConfirmModal({
      heading: t("application:sectionRollbackModal.heading"),
      text: t("application:sectionRollbackModal.text"),
      confirmButtonText: t(
        "application:sectionRollbackModal.confirmButtonText"
      ),
      cancelButtonText: t("application:sectionRollbackModal.cancelButtonText"),
      onConfirm: async () => {
        try {
          if (!formInstanceId) {
            throw new Error("formInstanceId is not provided");
          }
          await onRollback?.({ formInstanceId });
          showNotification({
            variant: "success",
            title: `${t("application:sectionRollback.success")}`,
          });
          trackEvent(Event.RollbackProfJourneyDetailsCompany);
          onClose();
        } catch (e) {
          showNotification({
            variant: "alert",
            title: `${t("application:failedToRollbackSection.title")}`,
          });
        }
      },
    });
  }, [t, formInstanceId, onRollback, onClose, trackEvent]);

  const handleCommentDelete = useCallback(
    async (commentId: string) => {
      showConfirmModal({
        heading: t("application:profilePage.client.confirmModal.heading"),
        text: t("application:profilePage.client.deleteConfirmText"),
        confirmButtonText: t(
          "application:profilePage.client.confirmModal.confirmButtonText"
        ),
        cancelButtonText: t(
          "application:profilePage.client.confirmModal.cancelButtonText"
        ),
        onConfirm: async () => {
          try {
            if (!commentId) {
              throw new Error("No comments to delete");
            }
            await deleteComment({
              applicationId: application.id,
              id: commentId,
            });
          } catch (e) {
            showNotification({
              variant: "alert",
              title: t(
                "application:profJourneyProjectDetailsModal.notification.failedToDeleteComment"
              ),
            });
          }
        },
      });
      return false;
    },
    [deleteComment, application.id, t]
  );

  const handleFinishReview = useCallback(async () => {
    if (!instanceId) {
      throw new Error("No project to finish review");
    }

    if (commentsToPublish?.length) {
      await showConfirmModal({
        heading: t(
          "application:profJourney.profJourneyProjectDetailsModal.finishReviewErrorDueComments.heading",
          { projectName }
        ),
        text: t(
          "application:profJourney.profJourneyProjectDetailsModal.finishReviewErrorDueComments.text"
        ),
        cancelButtonText: null,
        iconType: ConfirmModalIconType.Error,
      });
    } else {
      await showConfirmModal({
        heading: t(
          "application:profJourney.profJourneyProjectDetailsModal.finishReviewModal.heading",
          { projectName }
        ),
        text: t(
          "application:profJourney.profJourneyProjectDetailsModal.finishReviewModal.text"
        ),
        confirmButtonText: t(
          "application:profJourney.profJourneyProjectDetailsModal.finishReviewModal.confirmButtonText"
        ),
        onConfirm: async () => {
          await onFinishReview?.({
            projectName,
            formInstanceId: instanceId,
            experienceType,
            formRootId,
          });

          if (experienceType === ExperienceType.Employment) {
            trackEvent(Event.FinishReviewProject);
          } else if (experienceType === ExperienceType.Entrepreneurship) {
            trackEvent(Event.FinishReviewMilestone);
          }

          onClose();
        },
      });
    }
  }, [
    commentsToPublish,
    onFinishReview,
    projectName,
    experienceType,
    formRootId,
    instanceId,
    onClose,
    t,
    trackEvent,
  ]);

  return (
    <>
      <FormModal
        heading={
          <div className="flex items-center gap-3">
            {heading}
            {position && (
              <Badge size="150" type={BadgeType.Neutral}>
                {position}
              </Badge>
            )}
            {projectStatus && (
              <>
                {projectStatus ===
                  ApplicationSectionStatusType.SubmittedForReview ||
                projectStatus ===
                  ApplicationSectionStatusType.ReviewCompleted ? (
                  <Badge
                    className="mt-1.5"
                    size="150"
                    type={
                      projectStatus ===
                      ApplicationSectionStatusType.ReviewCompleted
                        ? BadgeType.Success
                        : BadgeType.Neutral
                    }
                  >
                    {projectStatus ===
                    ApplicationSectionStatusType.SubmittedForReview ? (
                      <>
                        <Icon24Clock className="mr-0.5 text-[16px]" />
                        {t("application:pageHeader.status.reviewing")}
                      </>
                    ) : projectStatus ===
                      ApplicationSectionStatusType.ReviewCompleted ? (
                      <>
                        <Icon24Check className="text-[16px]" />
                        {t("application:pageHeader.status.reviewcCompleted")}
                      </>
                    ) : (
                      ""
                    )}
                  </Badge>
                ) : null}
              </>
            )}
          </div>
        }
        isSelfService={false}
        open={open || false}
        fullScreen={true}
        innerClassName="gap-y-0"
        isAdmin={isAdmin(ownUser)}
        onCollapse={onClose}
        hasCompletedButton={true}
        scrollY={!formIsLoadingState.value}
        actionButton={
          formIsLoadingState.value ? (
            <></>
          ) : (
            <div className="flex gap-x-3">
              {editable ? (
                <>
                  <Button
                    className="inline-flex"
                    type={ButtonType.Primary}
                    disabled={showFormErrors && !isFormValid}
                    onClick={handeSubmitToReview}
                  >
                    {t(
                      "application:profJourney.profJourneyProjectDetailsModal.button.submitForReview"
                    )}
                  </Button>
                  <Button
                    className="inline-flex"
                    type={ButtonType.Secondary}
                    disabled={showFormErrors && !isFormValid}
                    onClick={handleSaveAndCloseClick}
                  >
                    {t(
                      "application:profJourney.profJourneyProjectDetailsModal.button.saveAndClose"
                    )}
                  </Button>
                </>
              ) : null}
              {isAdmin(ownUser) && (
                <>
                  {projectStatus ===
                    ApplicationSectionStatusType.SubmittedForReview && (
                    <Button
                      className="inline-flex"
                      type={ButtonType.Primary}
                      onClick={handleFinishReview}
                    >
                      {t(
                        "application:profJourney.profJourneyProjectDetailsModal.button.finishReview"
                      )}
                    </Button>
                  )}

                  {projectStatus !== ApplicationSectionStatusType.Created && (
                    <Button
                      className="inline-flex"
                      type={ButtonType.Secondary}
                      onClick={handleSectionRollback}
                    >
                      {t("application:navigation.button.rollback")}
                    </Button>
                  )}
                  {isUnsentComments && (
                    <Button
                      className="inline-flex"
                      type={ButtonType.Secondary}
                      onClick={handleSendFeedback}
                    >
                      {t(
                        "application:profJourney.profJourneyProjectDetailsModal.button.sendFeedBack"
                      )}
                    </Button>
                  )}
                </>
              )}
            </div>
          )
        }
      >
        <div
          className={clsxm(
            "w-full flex-grow items-start pb-[70px] sm:grid sm:grid-cols-6 lg:mx-auto lg:grid-cols-12 lg:gap-8"
          )}
        >
          <Comments
            upsertComment={async (comment) => {
              await upsertComment({
                applicationId: application.id,
                data: {
                  ...comment,
                  group: instanceId,
                } as any,
              });
              return comment;
            }}
            deleteComment={handleCommentDelete}
            isManager={isAdmin(ownUser)}
            comments={comments || ([] as IComment[])}
          >
            {() => (
              <ImmigramFormConnected
                externalFormValues={externalFormValues}
                showValidationErrors={showFormErrors}
                onIsValidChange={setIsFormValid}
                className="col-span-12"
                placeholderClassName="col-span-7"
                applicationId={application.id}
                formInstanceId={formInstanceId}
                formNamespace={formNamespace}
                formParentId={formParentId}
                onFormInstanceCreate={handleFormInstanceCreate}
                onFieldChange={onFieldChange}
                onLoadingStart={formIsLoadingState.setTrue}
                onLoadingEnd={formIsLoadingState.setFalse}
                editable={editable}
                withComments={true}
                isO1Visa={application.visaTypeId === 2}
              />
            )}
          </Comments>
        </div>
      </FormModal>
      <ValidationModal
        gifUrl="/images/gif/4giphy.gif"
        open={isValidationModalOpen}
        heading={t("application:profJourney.details.ValidationModal.heading")}
        text={t("application:profJourney.details.ValidationModal.text")}
        onCollapse={() => setIsValidationModalOpen(false)}
      />
    </>
  );
}
